/**
 * Один код возражения на экране «Возражения» (#846): строка с числами взгляда
 * и раскрытие (`ObjectionCodeDetails`).
 *
 * Доля в строке и её сдвиг — от одного знаменателя (разговоры с распознанным
 * возражением), иначе строка показывала бы «20 %» рядом с «▲ 10 п.п.» от 30 %.
 *
 * В разделе «Голос клиента» (#2394 §9 п. 7, #2556) строка взгляда рынка несёт
 * спарклайн ряда кода, а раскрытие начинается с его графика.
 */
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { ObjectionStreamCode } from '@aica/api-types/schemas';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { localizeObjectionCode } from '@/components/dashboards/objectionCodeNames';
import { ObjectionCodeDetails } from './ObjectionCodeDetails';
import {
  markedObjectionTrend,
  objectionTrend,
  percent,
  type ObjectionsViewMode,
  type TrendBase,
} from './objection-view-model';
import { useVoiceComparable } from '../voice/voice-theme-filter-context';
import type { VoiceRowDetailsTrend } from '../voice/voice-series-view-model';
import { VoiceSparkline } from '../voice/VoiceSparkline';
import { seriesHasPoints } from '../voice/voice-series-view-model';
import { NO_TREND } from '../voice/voice-theme-filter-view-model';

interface ObjectionCodeCardProps {
  code: ObjectionStreamCode;
  mode: ObjectionsViewMode;
  /** Denominators of the share and its shift — recognized calls only. */
  trendBase: TrendBase;
  expanded: boolean;
  onToggle: () => void;
  onOpenCall: (recordId: string, quote: string) => void;
  /**
   * The code's series in the Voice section (#2394 §9 п. 7): a sparkline by
   * the share, and the expanded card's chart whose bucket narrows the quotes.
   */
  series?: VoiceRowDetailsTrend;
  /**
   * The stream asked the rule Р3 marks (the Voice section, #2698): «new» is
   * the server's mark, as in «What changed», not the previous window's zero.
   */
  marked?: boolean;
}

export function ObjectionCodeCard({
  code,
  mode,
  trendBase,
  expanded,
  onToggle,
  onOpenCall,
  series,
  marked = false,
}: ObjectionCodeCardProps) {
  const { t } = useTranslation(['dashboard', 'records']);
  const name = localizeObjectionCode(code.code, code.wordings[0] ?? code.code, t);
  const share = percent(code.calls, trendBase.recognizedCalls);
  // In the Voice section under a theme filter whose previous window is not
  // comparable (#2229): no trend — the filter strip says why.
  const comparable = useVoiceComparable();
  const trendOf = marked ? markedObjectionTrend : objectionTrend;
  const trend = comparable ? trendOf(code, trendBase) : NO_TREND;
  const detailsId = `objection-${code.code}-details`;
  // The series is the market view's: the team view counts unhandled.
  const seriesTrend = mode === 'market' ? series : undefined;

  return (
    // A container: the sparkline hides by the card's width (#2394 §6).
    <div className="@container rounded-card border border-border bg-bg-surface">
      <button
        type="button"
        className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-expanded={expanded}
        aria-controls={detailsId}
        onClick={onToggle}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-fg-muted" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-fg-muted" />
        )}
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">
          {name}
        </span>
        {mode === 'market' ? (
          <>
            {trend.kind === 'new' && (
              <Badge variant="info">{t('objectionsPage.trend.new')}</Badge>
            )}
            {trend.kind === 'change' && trend.delta !== 0 && (
              <span
                className={cn(
                  'font-mono text-xs',
                  trend.delta > 0 ? 'text-warning' : 'text-success'
                )}
                title={t('objectionsPage.trend.prevShare', { share: trend.prevShare })}
              >
                {t(
                  trend.delta > 0
                    ? 'objectionsPage.trend.up'
                    : 'objectionsPage.trend.down',
                  {
                    delta: Math.abs(trend.delta),
                  }
                )}
              </span>
            )}
            {seriesTrend && seriesHasPoints(seriesTrend.series) && (
              <VoiceSparkline series={seriesTrend.series} unit={seriesTrend.unit} />
            )}
            <span className="font-mono text-xs text-fg-muted">
              {t('objectionsPage.market.calls', { count: code.calls, share })}
            </span>
          </>
        ) : (
          <>
            {code.avgScore !== null && (
              <span className="font-mono text-xs text-fg-muted">
                {t('objectionsPage.team.avgScore', { score: code.avgScore })}
              </span>
            )}
            <span
              className={cn(
                'font-mono text-xs font-semibold',
                code.unhandledCalls > 0 ? 'text-danger' : 'text-fg-muted'
              )}
            >
              {t('objectionsPage.team.unhandled', {
                unhandled: code.unhandledCalls,
                count: code.calls,
              })}
            </span>
          </>
        )}
      </button>

      {expanded && (
        <ObjectionCodeDetails
          // A new view starts on the whole period, as a reopened row does.
          key={mode}
          id={detailsId}
          code={code}
          mode={mode}
          series={seriesTrend}
          onOpenCall={onOpenCall}
        />
      )}
    </div>
  );
}
